import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { BedDouble } from "lucide-react";

const STATUS_COLOR = {
  occupied:    "bg-[#B71C1C] text-white",
  vacant:      "bg-emerald-100 text-[#1B5E20]",
  cleaning:    "bg-amber-100 text-[#B45309]",
  maintenance: "bg-slate-200 text-slate-600",
  reserved:    "bg-blue-100 text-[#1565C0]",
};
const STATUS_LABEL = {
  occupied: "Occupied", vacant: "Free", cleaning: "Cleaning",
  maintenance: "Maintenance", reserved: "Reserved",
};

// "ready in N min" hint for beds that were released to housekeeping on discharge.
// Backend stamps `ready_at` = now + 30min; we count down against it.
function readyHint(bed) {
  if (bed.status !== "cleaning" || !bed.ready_at) return null;
  const ms = new Date(bed.ready_at).getTime() - Date.now();
  if (ms <= 0) return "ready any moment";
  const mins = Math.max(1, Math.round(ms / 60000));
  return `ready in ${mins} min`;
}

const MANAGE_ROLES = ["hospital_admin", "super_admin"];

function BedContextMenu({ x, y, bed, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const setStatus = async (status) => {
    if (bed.status === status && status !== "occupied") { onClose(); return; }
    try {
      setSaving(true);
      await api.post("/beds/status", { bed_id: bed.bed_id, status });
      toast.success(`${bed.bed_no} → ${STATUS_LABEL[status] || status}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update bed");
    } finally { setSaving(false); }
  };

  // Constrain to viewport
  const menuW = 200, menuH = 210;
  const left = Math.min(x, (typeof window !== "undefined" ? window.innerWidth : 1200) - menuW - 8);
  const top  = Math.min(y, (typeof window !== "undefined" ? window.innerHeight : 800) - menuH - 8);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        className="fixed z-50 bg-white rounded-lg shadow-xl border border-slate-200 py-1 w-[200px]"
        style={{ left, top }}
        data-testid="bed-context-menu"
      >
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-xs font-semibold text-slate-800">{bed.bed_no}</div>
          <div className="text-[11px] text-slate-500">{bed.ward} · {STATUS_LABEL[bed.status] || bed.status}</div>
        </div>
        {["vacant", "cleaning", "maintenance", "reserved"].map((s) => (
          <button
            key={s}
            data-testid={`bed-set-${s}`}
            disabled={saving || bed.status === s}
            onClick={() => setStatus(s)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent flex items-center gap-2 ${bed.status === s ? "font-semibold text-slate-900" : "text-slate-700"}`}
          >
            <span className={`w-3 h-3 rounded ${STATUS_COLOR[s]}`} />
            Set {STATUS_LABEL[s]}
          </button>
        ))}
      </div>
    </>
  );
}

export function BedBoardStrip({ refreshKey = 0 }) {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [data, setData] = useState({ beds: [], wards: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [localKey, setLocalKey] = useState(0);
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/beds");
        if (alive) setData(data || { beds: [], wards: {} });
      } catch (err) { console.debug("bed board fetch failed", err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [refreshKey, localKey]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-4 text-sm text-slate-500" data-testid="bedboard-loading">
        Loading bed board…
      </div>
    );
  }

  const wards = Object.keys(data.wards || {}).sort();
  if (wards.length === 0) return null;

  const bedsByWard = wards.reduce((acc, w) => {
    acc[w] = (data.beds || [])
      .filter((b) => b.ward === w)
      .sort((a, b) => (a.bed_no || "").localeCompare(b.bed_no || "", undefined, { numeric: true }));
    return acc;
  }, {});

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="bed-board-strip">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BedDouble className="w-4 h-4 text-[#1976D2]" />
          <div className="text-sm font-semibold text-slate-800">Bed Board</div>
          <div className="hidden md:flex items-center gap-2 ml-3 text-[11px]">
            {["occupied","vacant","cleaning","maintenance","reserved"].map((s) => (
              <span key={s} className="flex items-center gap-1 text-slate-500">
                <span className={`w-3 h-3 rounded ${STATUS_COLOR[s]}`}></span>{STATUS_LABEL[s]}
              </span>
            ))}
          </div>
          {canManage && (
            <span className="text-[11px] text-slate-400 hidden lg:inline">· right-click a bed to change its status</span>
          )}
        </div>
        <button data-testid="bed-board-toggle" onClick={() => setExpanded((v) => !v)} className="h-7 px-2.5 rounded-md text-[11px] font-medium text-[#1565C0] hover:bg-blue-50">
          {expanded ? "Collapse" : "Expand grid"}
        </button>
      </div>

      <div className={`grid gap-3 ${wards.length <= 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"}`}>
        {wards.map((w) => {
          const stats = data.wards[w];
          const total = stats.total || 0;
          const occ = stats.occupied || 0;
          const free = stats.vacant || 0;
          const pct = total > 0 ? Math.round((occ / total) * 100) : 0;
          const barTone = pct >= 90 ? "bg-[#B71C1C]" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div key={w} className="rounded-lg border border-slate-200 p-3" data-testid={`bedboard-ward-${w}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">{w}</div>
                <div className="text-[11px] text-slate-500 tabular">{occ}/{total} · {free} free</div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
              </div>
              {expanded && (
                <div className="mt-3 flex flex-wrap gap-1" data-testid={`bedboard-grid-${w}`}>
                  {bedsByWard[w].map((b) => {
                    const hint = readyHint(b);
                    return (
                      <span
                        key={b.bed_id}
                        title={`${b.bed_no} · ${STATUS_LABEL[b.status] || b.status}${b.patient_name ? ` · ${b.patient_name}` : ""}${hint ? ` · ${hint}` : ""}${canManage ? " · right-click to change" : ""}`}
                        data-testid={`bed-cell-${b.bed_no}`}
                        onContextMenu={canManage ? (e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, bed: b }); } : undefined}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium tabular ${STATUS_COLOR[b.status] || "bg-slate-100 text-slate-500"} ${canManage ? "cursor-context-menu" : ""}`}
                      >{b.bed_no}{hint ? ` · ${hint}` : ""}</span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {menu && (
        <BedContextMenu
          x={menu.x} y={menu.y} bed={menu.bed}
          onClose={() => setMenu(null)}
          onDone={() => { setMenu(null); setLocalKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
